import {deleteDoc, doc, getFirestore, serverTimestamp, setDoc} from "firebase/firestore";
import Firebase from "../Firebase/Firebase";

/** Browser push for admins (standard Web Push; sent by functions/web_push.js). */
const VAPID_PUBLIC_KEY = 'BCSCuhAmpwywfat10bxop7B2BDRJ_ZvTLkCoCGECFADw2q5qLKLbSRdZDi1kTJXmbX_3bNps_803AKCyCY99BXw';
const db = getFirestore(Firebase);

export const isIOS = () => /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
/** Added to the Home Screen and opened from there (required for push on iPhone). */
export const isStandalone = () => window.matchMedia('(display-mode: standalone)').matches || (navigator as any).standalone === true;
export const pushSupported = () => 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;

function keyBytes(base64: string) {
    const pad = '='.repeat((4 - (base64.length % 4)) % 4);
    const raw = atob((base64 + pad).replace(/-/g, '+').replace(/_/g, '/'));
    return Uint8Array.from(raw, (c) => c.charCodeAt(0));
}

async function subId(endpoint: string) {
    const hash = await crypto.subtle.digest('SHA-1', new TextEncoder().encode(endpoint));
    return Array.from(new Uint8Array(hash)).map((b) => b.toString(16).padStart(2, '0')).join('');
}

export async function registerServiceWorker() {
    if (!('serviceWorker' in navigator)) return null;
    try {
        return await navigator.serviceWorker.register('/sw.js');
    } catch {
        return null;
    }
}

export async function currentSubscription(): Promise<PushSubscription | null> {
    if (!pushSupported()) return null;
    const reg = await navigator.serviceWorker.getRegistration();
    return (await reg?.pushManager.getSubscription()) ?? null;
}

/** Asks for permission (must be called from a tap) and saves this browser for [uid]. */
export async function enableAdminPush(uid: string): Promise<'on' | 'denied' | 'unsupported'> {
    if (!pushSupported()) return 'unsupported';
    const permission = await Notification.requestPermission();
    if (permission !== 'granted') return 'denied';
    const reg = (await navigator.serviceWorker.getRegistration()) ?? (await registerServiceWorker());
    if (!reg) return 'unsupported';
    await navigator.serviceWorker.ready;
    const sub = (await reg.pushManager.getSubscription())
        ?? (await reg.pushManager.subscribe({userVisibleOnly: true, applicationServerKey: keyBytes(VAPID_PUBLIC_KEY)}));
    const json = sub.toJSON();
    await setDoc(doc(db, 'webPushSubscriptions', await subId(sub.endpoint)), {
        userId: uid,
        app: 'admin',
        endpoint: sub.endpoint,
        keys: {p256dh: json.keys?.p256dh ?? '', auth: json.keys?.auth ?? ''},
        userAgent: navigator.userAgent.slice(0, 200),
        createdAt: serverTimestamp(),
    });
    return 'on';
}

/** Stops push on this browser (also on logout, so the next person doesn't get admin alerts). */
export async function disableAdminPush() {
    const sub = await currentSubscription();
    if (!sub) return;
    await deleteDoc(doc(db, 'webPushSubscriptions', await subId(sub.endpoint))).catch(() => {});
    await sub.unsubscribe().catch(() => {});
}
